// src/components/DashboardTabs/MediaTab.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import NeonButton from "../ResponsiveButton/NeonButton.jsx";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog.jsx";
import { mediaUrl } from "../../utils/media.js";

// --- Gallerian kuville: muunna WEBP:ksi ja pakkaa < 5 Mt ---
const GALLERY_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const GALLERY_MAX_SIDE = 2560;             // tarvittaessa skaalaa pienemmäksi

function fileToHTMLImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Kuvan lataus epäonnistui.")); };
    img.src = url;
  });
}

function canvasToBlob(canvas, type = "image/webp", quality = 0.92) {
  return new Promise((resolve, reject) => {
    if (!canvas.toBlob) {
      try {
        const dataUrl = canvas.toDataURL(type, quality);
        const byteString = atob(dataUrl.split(",")[1]);
        const mime = dataUrl.split(";")[0].split(":")[1];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        resolve(new Blob([ab], { type: mime }));
      } catch (e) {
        reject(e);
      }
      return;
    }
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Muunnos epäonnistui."))),
      type,
      quality
    );
  });
}

/**
 * Palauttaa File-olion (image/webp), joka on enintään 5 Mt.
 * 1) Skalaa tarvittaessa niin, että pisin sivu <= GALLERY_MAX_SIDE
 * 2) Pienennä laatua portaittain, kunnes <= 5 Mt
 * 3) Jos ei riitä, pienennä resoluutiota ja yritä uudelleen
 */
async function convertImageToWebpUnderLimit(inputFile) {
  if (inputFile.type === "image/webp" && inputFile.size <= GALLERY_MAX_BYTES) {
    return inputFile;
  }
  const img = await fileToHTMLImage(inputFile);

  let scale = Math.min(1, GALLERY_MAX_SIDE / Math.max(img.width, img.height));
  let attempt = 0;
  while (attempt < 6) {
    const canvas = document.createElement("canvas");
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    let quality = 0.92;
    for (let i = 0; i < 7; i++) {
      const blob = await canvasToBlob(canvas, "image/webp", quality);
      if (blob.size <= GALLERY_MAX_BYTES) {
        return new File([blob], inputFile.name.replace(/\.\w+$/, ".webp"), {
          type: "image/webp",
          lastModified: Date.now(),
        });
      }
      quality -= 0.1;
      if (quality < 0.45) break;
    }

    scale *= 0.85;
    attempt++;
  }

  throw new Error("Kuva on poikkeuksellisen iso – yritä pienemmällä resoluutiolla.");
}

/* ---------- YouTube/Vimeo/SoundCloud apurit ---------- */
function ytStartSeconds(u) {
  const t = u.searchParams.get("t") || u.searchParams.get("start") || "";
  if (!t) return 0;
  const secs = /^(?<num>\d+)(s)?$/i.test(t)
    ? parseInt(t, 10)
    : (() => {
        const h = /(\d+)h/i.exec(t)?.[1] ?? 0;
        const m = /(\d+)m/i.exec(t)?.[1] ?? 0;
        const s = /(\d+)s/i.exec(t)?.[1] ?? 0;
        return (+h) * 3600 + (+m) * 60 + (+s);
      })();
  return Number.isFinite(secs) && secs > 0 ? secs : 0;
}

function toEmbedUrl(raw) {
  if (!raw) return "";
  try {
    const u = new URL(raw);

    const isYtHost =
      /(^|\.)youtube\.com$/i.test(u.hostname) ||
      /^m\.youtube\.com$/i.test(u.hostname) ||
      /^youtu\.be$/i.test(u.hostname);

    if (isYtHost) {
      let id = null;
      if (/^youtu\.be$/i.test(u.hostname)) {
        id = u.pathname.replace(/^\/+/, "").split(/[/?#]/)[0];
      } else {
        id =
          u.searchParams.get("v") ||
          (u.pathname.startsWith("/live/") ? u.pathname.split("/")[2] : "") ||
          (u.pathname.startsWith("/shorts/") ? u.pathname.split("/")[2] : "") ||
          (u.pathname.startsWith("/embed/") ? u.pathname.split("/")[2] : "");
      }
      const start = ytStartSeconds(u);
      if (id) return `https://www.youtube.com/embed/${id}${start ? `?start=${start}` : ""}`;
      return "";
    }

    if (/(^|\.)vimeo\.com$/i.test(u.hostname)) {
      if (/(^|\.)player\.vimeo\.com$/i.test(u.hostname) && u.pathname.startsWith("/video/")) {
        return raw;
      }
      const m = u.pathname.match(/\/(\d+)/);
      if (m?.[1]) return `https://player.vimeo.com/video/${m[1]}`;
    }
  } catch {}
  return "";
}

function toScEmbed(raw) {
  try {
    const u = new URL(raw);
    if (!/soundcloud\.com$/i.test(u.hostname) && !/\.soundcloud\.com$/i.test(u.hostname)) return "";
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(
      raw
    )}&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
  } catch {
    return "";
  }
}

// --- Debounce-apuri esikatseluun (vähentää "räpsyä") ---
function useDebouncedValue(value, delay = 450) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// --- Stabiili InfoBox moduulin juureen (ei remounttaa joka renderillä) ---
const InfoBox = React.memo(function InfoBox({ title, help, children }) {
  return (
    <section>
      <h3 className="block dark:text-white/90 text-black/90 font-customFont2 text-lg mb-1 font-bold">
        {title}
      </h3>
      {help && <p className="text-xs mb-2 text-black/60 dark:text-white/60">{help}</p>}
      {children}
    </section>
  );
});

/* ===================== Komponentti ===================== */
export default function MediaTab({ band, exposeSave, onSaved }) {

  const MAX_IMAGES = 4;
  const MAX_VIDEOS = 2;
  const MAX_AUDIOS = 4;

  // --- Lomakekentät URL-lisäyksiin ---
  const [videoUrl, setVideoUrl] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const fileRef = useRef(null);

  // --- Palvelimelta tuleva nykytila ---
  const [gallery, setGallery] = useState(() =>
    Array.isArray(band?.gallery)
      ? band.gallery.map((g) => ({ id: g.id, url: g.url || g.image_url || g }))
      : []
  );
  const [videos, setVideos] = useState(() =>
    Array.isArray(band?.videos)
      ? band.videos.map((v) => ({ id: v.id, url: v.url || v.video_url }))
      : []
  );
  const [audios, setAudios] = useState(() =>
    Array.isArray(band?.audio)
      ? band.audio.map((a) => ({ id: a.id, url: a.url || a.track_url }))
      : []
  );

  // --- Puskurimuutokset (ei vielä tallennettu) ---
  const [newImages, setNewImages] = useState([]);            // [{_tmpId, file, preview}]
  const [removeImageIds, setRemoveImageIds] = useState(new Set());

  const [newVideos, setNewVideos] = useState([]);            // [{_tmpId, url}]
  const [removeVideoIds, setRemoveVideoIds] = useState(new Set());

  const [newAudios, setNewAudios] = useState([]);            // [{_tmpId, url}]
  const [removeAudioIds, setRemoveAudioIds] = useState(new Set());

  const [saving, setSaving] = useState(false);

  // Kun bändi vaihtuu → hydraa perusdata
  useEffect(() => {
    setGallery(
      Array.isArray(band?.gallery)
        ? band.gallery.map((g) => ({ id: g.id, url: g.url || g.image_url || g }))
        : []
    );
    setVideos(
      Array.isArray(band?.videos)
        ? band.videos.map((v) => ({ id: v.id, url: v.url || v.video_url }))
        : []
    );
    setAudios(
      Array.isArray(band?.audio)
        ? band.audio.map((a) => ({ id: a.id, url: a.url || a.track_url }))
        : []
    );
    // nollaa puskuri – jätetään kenttiin kirjoitetut URLit rauhaan
    setNewImages([]); setRemoveImageIds(new Set());
    setNewVideos([]); setRemoveVideoIds(new Set());
    setNewAudios([]); setRemoveAudioIds(new Set());
  }, [band?.id]);

  // Debouncetut esikatselut (vähemmän iframe-mountteja kirjoittaessa)
  const debVideoUrl = useDebouncedValue(videoUrl, 450);
  const debAudioUrl = useDebouncedValue(audioUrl, 450);
  const embedPreviewUrl = useMemo(() => toEmbedUrl(debVideoUrl), [debVideoUrl]);
  const scPreviewUrl = useMemo(() => toScEmbed(debAudioUrl), [debAudioUrl]);

  // Yhdistetyt listat näkymää varten
  const visibleGallery = useMemo(() => {
    const base = gallery.filter((g) => !removeImageIds.has(g.id));
    return [...base, ...newImages];
  }, [gallery, removeImageIds, newImages]);

  const visibleVideos = useMemo(() => {
    const base = videos.filter((v) => !removeVideoIds.has(v.id));
    return [...base, ...newVideos];
  }, [videos, removeVideoIds, newVideos]);

  const visibleAudios = useMemo(() => {
    const base = audios.filter((a) => !removeAudioIds.has(a.id));
    return [...base, ...newAudios];
  }, [audios, removeAudioIds, newAudios]);

  // Kapasiteettirajat puskuri huomioiden
  const canAddImage = (visibleGallery.length) < MAX_IMAGES;
  const canAddVideo = (visibleVideos.length) < MAX_VIDEOS;
  const canAddAudio = (visibleAudios.length) < MAX_AUDIOS;

  const onPick = async (e) => {
    const src = e.target.files?.[0];
    e.target.value = "";
    if (!src) return;

    const okType = ["image/png", "image/webp", "image/jpeg"].includes(src.type);
    if (!okType) {
      return toast.error("Vain WEBP, PNG tai JPG sallittu.", { containerId: "global" });
    }
    if (!canAddImage) {
      return toast.info(`Galleriassa on jo ${MAX_IMAGES} kuvaa.`, { containerId: "global" });
    }
    try {
      const webpFile = await convertImageToWebpUnderLimit(src);
      const preview = URL.createObjectURL(webpFile);
      setNewImages((prev) => [
        ...prev,
        { _tmpId: `new_${Date.now()}_${Math.random()}`, file: webpFile, preview }
      ]);
      toast.success("Kuva muunnettu WEBP:ksi (≤ 5 Mt).", { containerId: "global" });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Kuvan muunnos epäonnistui.", { containerId: "global" });
    }
  };

  // --- Lisäykset puskuriin URL-kentistä ---
  const addVideoLocal = () => {
    const raw = (videoUrl || "").trim();
    const embed = toEmbedUrl(raw);
    if (!embed) return toast.error("Anna YouTube- tai Vimeo-linkki.", { containerId: "global" });
    if (!canAddVideo) return toast.info(`Maks. ${MAX_VIDEOS} videota.`, { containerId: "global" });
    setNewVideos((prev) => [...prev, { _tmpId: `v_${Date.now()}_${Math.random()}`, url: raw }]);
    setVideoUrl("");
  };

  const addAudioLocal = () => {
    const raw = (audioUrl || "").trim();
    if (!/^https?:\/\//i.test(raw)) return toast.error("Anna kelvollinen audiolinkki.", { containerId: "global" });
    if (!toScEmbed(raw)) return toast.error("Tällä hetkellä tuetaan SoundCloud-linkkejä.", { containerId: "global" });
    if (!canAddAudio) return toast.info(`Maks. ${MAX_AUDIOS} ääniraitaa.`, { containerId: "global" });
    setNewAudios((prev) => [...prev, { _tmpId: `a_${Date.now()}_${Math.random()}`, url: raw }]);
    setAudioUrl("");
  };

  // --- Poistot (merkitse puskuriin) ---
  const markRemoveImage = (item) => {
    if (item?.id) setRemoveImageIds((s) => new Set([...s, item.id]));
    if (item?._tmpId) {
      setNewImages((prev) => {
        const rem = prev.find((p) => p._tmpId === item._tmpId);
        if (rem?.preview) URL.revokeObjectURL(rem.preview);
        return prev.filter((p) => p._tmpId !== item._tmpId);
      });
    }
  };
  const markRemoveVideo = (item) => {
    if (item?.id) setRemoveVideoIds((s) => new Set([...s, item.id]));
    if (item?._tmpId) setNewVideos((prev) => prev.filter((p) => p._tmpId !== item._tmpId));
  };
  const markRemoveAudio = (item) => {
    if (item?.id) setRemoveAudioIds((s) => new Set([...s, item.id]));
    if (item?._tmpId) setNewAudios((prev) => prev.filter((p) => p._tmpId !== item._tmpId));
  };

  // --- Confirm dialogien tilat ---
  const [confirmImg, setConfirmImg] = useState({ open: false, item: null });
  const [confirmVid, setConfirmVid] = useState({ open: false, item: null });
  const [confirmAud, setConfirmAud] = useState({ open: false, item: null });

  // --- Alapalkin TALLENNA ---
  const onSave = useCallback(async () => {
    if (saving) return;

    const hasChanges =
      newImages.length || removeImageIds.size ||
      newVideos.length || removeVideoIds.size ||
      newAudios.length || removeAudioIds.size;

    if (!hasChanges) {
      toast.info("Ei tallennettavia muutoksia.", { containerId: "global" });
      return;
    }

    setSaving(true);
    try {
      // 1) Poistot
      await Promise.all([
        ...Array.from(removeImageIds).map((id) => api.media.deleteGalleryImage(band.id, id).catch(() => {})),
        ...Array.from(removeVideoIds).map((id) => api.media.deleteVideo(band.id, id).catch(() => {})),
        ...Array.from(removeAudioIds).map((id) => api.media.deleteAudio(band.id, id).catch(() => {})),
      ]);

      // 2) Lisäykset
      for (const img of newImages)   await api.media.uploadGalleryImage(band.id, img.file);
      for (const v of newVideos)     await api.media.addVideo(band.id, v.url);
      for (const a of newAudios)     await api.media.addAudio(band.id, a.url);

      // 3) Hae tuore data + nollaa puskuri
      let fresh = null;
      try {
        fresh = await api.bandByIdOrSlug(band.id);

        setGallery(
          Array.isArray(fresh?.gallery)
            ? fresh.gallery.map((g) => ({ id: g.id, url: g.url || g.image_url || g }))
            : []
        );
        setVideos(
          Array.isArray(fresh?.videos)
            ? fresh.videos.map((v) => ({ id: v.id, url: v.url || v.video_url }))
            : []
        );
        setAudios(
          Array.isArray(fresh?.audio)
            ? fresh.audio.map((a) => ({ id: a.id, url: a.url || a.track_url }))
            : []
        );
      } catch (e) {
        console.error("Refresh failed", e);
      }

      // Tyhjennä puskuroitu tila ja urlyt
      newImages.forEach((i) => i.preview && URL.revokeObjectURL(i.preview));
      setNewImages([]); setRemoveImageIds(new Set());
      setNewVideos([]); setRemoveVideoIds(new Set());
      setNewAudios([]); setRemoveAudioIds(new Set());
      setVideoUrl(""); setAudioUrl("");
      onSaved?.(fresh);
      toast.success("Media tallennettu.", { containerId: "global" });
    } catch (e) {
      console.error(e);
      toast.error("Tallennus epäonnistui.", { containerId: "global" });
    } finally {
      setSaving(false);
    }
  }, [saving, band.id, newImages, removeImageIds, newVideos, removeVideoIds, newAudios, removeAudioIds, onSaved]);

  // Rekisteröi sticky action barille
  useEffect(() => {
    exposeSave?.(onSave);
    return () => exposeSave?.(null);
  }, [exposeSave, onSave]);

  /* -------------------- RENDER -------------------- */
  return (
    <div className="space-y-6 p-4">
      {/* Kuvat */}
      <InfoBox
        title="Kuvat galleriassa"
        help="Lisää kuvia ja tallenna alapalkista. Maks. 4 kuvaa. JPG/PNG muunnetaan automaattisesti WEBP:ksi (≤ 5 Mt)."
      >
        <div className="mb-3 flex items-center gap-3 pt-4 pb-4">
          <NeonButton
            type="button"
            size="sm"
            className="h-10 min-w-32"
            disabled={!canAddImage}
            onClick={() => canAddImage && fileRef.current?.click()}
          >
            Lisää kuva
          </NeonButton>
          <span className="text-xs dark:text-white/80 text-black/80 font-customFont1">
            Maks. {MAX_IMAGES} kuvaa · JPG/PNG muunnetaan automaattisesti WEBP:ksi (≤ 5 Mt).
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/webp,image/jpeg,image/jpg"
            className="hidden"
            onChange={onPick}
            disabled={!canAddImage}
          />
        </div>

        {visibleGallery.length ? (
          <ul className="grid grid-cols-2 md:grid-cols-4 mdd:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-3 pt-4 pb-4">
            {visibleGallery.map((g) => {
              const isNew = !!g._tmpId;
              const src = isNew ? g.preview : mediaUrl(g.url);
              return (
                <li key={g.id || g._tmpId || g.url} className="group relative aspect-video overflow-hidden rounded-md bg-black/10">
                  <img src={src} className="w-full h-full object-cover" loading="lazy" decoding="async" alt="" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/40 to-transparent" />
                  <button
                    type="button"
                    onClick={() => setConfirmImg({ open: true, item: g })}
                    title="Poista kuva"
                    className="absolute top-2 right-2 z-30 h-8 px-2 text-xs rounded-md bg-black/70 text-white shadow-md ring-1 ring-white/30 hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-pink"
                  >
                    Poista
                  </button>
                  {isNew && (
                    <span className="absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-primary/80 text-white">
                      Uusi
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-black/60 dark:text-white/60">Ei kuvia.</p>
        )}
      </InfoBox>

      {/* Videot */}
      <InfoBox
        title="Videot"
        help="Liitä YouTube- tai Vimeo-linkki ja paina ”Lisää video”. Muista tallentaa alapalkista. Maks. 2 videota."
      >
        <form
          className="mb-3 flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs"
          onSubmit={(e) => { e.preventDefault(); if (canAddVideo) addVideoLocal(); }}
        >
          <input
            type="url"
            className="w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10"
            placeholder="https://www.youtube.com/.."
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            aria-label="Videon osoite"
            autoComplete="off"
            spellCheck={false}
          />
          <NeonButton type="submit" size="sm" disabled={!canAddVideo}>
            Lisää video
          </NeonButton>
        </form>

        <div className="grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-3 pt-4 pb-4">
          {embedPreviewUrl && (
            <div className="aspect-video overflow-hidden rounded-md ring-1 ring-black/10 dark:ring-white/10 bg-black/20">
              <iframe
                key={`preview-${embedPreviewUrl}`}
                src={embedPreviewUrl}
                title="Videon esikatselu"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full"
              />
              <div className="px-2 py-1 text-xs opacity-70">Esikatselu — paina “Lisää video”.</div>
            </div>
          )}

          {visibleVideos.map((v) => {
            const isNew = !!v._tmpId;
            const src = toEmbedUrl(v.url);
            return (
              <div key={v.id || v._tmpId || v.url} className="relative aspect-video overflow-hidden rounded-md ring-1 ring-black/10 dark:ring-white/10 bg-black/20">
                {src ? (
                  <iframe
                    key={src}
                    src={src}
                    title="Video"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <a href={v.url} target="_blank" rel="noreferrer" className="underline block p-3 text-sm">
                    {v.url}
                  </a>
                )}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/40 to-transparent" />
                <button
                  type="button"
                  onClick={() => setConfirmVid({ open: true, item: v })}
                  className="absolute top-2 right-2 z-30 h-8 px-2 text-xs rounded-md bg-black/70 text-white shadow-md ring-1 ring-white/30 hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-pink"
                >
                  Poista
                </button>
                {isNew && <span className="absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-primary/80 text-white">Uusi</span>}
              </div>
            );
          })}
        </div>

        {!visibleVideos.length && !embedPreviewUrl && (
          <p className="mt-2 text-sm text-black/60 dark:text-white/60">Ei videoita.</p>
        )}
      </InfoBox>

      {/* Audiot */}
      <InfoBox
        title="Ääniraidat (SoundCloud)"
        help="Liitä SoundCloud-linkki ja paina ”Lisää audio”. Muista tallentaa alapalkista. Maks. 4 raitaa."
      >
        <form
          className="mb-3 flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs"
          onSubmit={(e) => { e.preventDefault(); if (canAddAudio) addAudioLocal(); }}
        >
          <input
            type="url"
            className="w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10"
            placeholder="https://soundcloud.com/…"
            value={audioUrl}
            onChange={(e) => setAudioUrl(e.target.value)}
            aria-label="SoundCloud-osoite"
            autoComplete="off"
            spellCheck={false}
          />
          <NeonButton type="submit" size="sm" disabled={!canAddAudio}>
            Lisää audio
          </NeonButton>
        </form>

        {scPreviewUrl && (
          <div className="overflow-hidden rounded-md ring-1 ring-black/10 dark:ring-white/10 bg-black/20 ">
            <iframe
              key={`preview-${scPreviewUrl}`}
              title="Audion esikatselu"
              className="w-full"
              height="166"
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
              src={scPreviewUrl}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <div className="px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-xs opacity-70">
              Esikatselu — paina “Lisää audio”.
            </div>
          </div>
        )}

        {visibleAudios.length ? (
          <ul className="grid grid-cols-1 xl:grid-cols-2  2xl:grid-cols-2 gap-3 pt-4 pb-4">
            {visibleAudios.map((a) => {
              const isNew = !!a._tmpId;
              const src = toScEmbed(a.url);
              return (
                <li key={a.id || a._tmpId || a.url} className="relative overflow-hidden rounded-md ring-1 ring-black/10 dark:ring-white/10 bg-black/20">
                  {src ? (
                    <iframe
                      key={src}
                      title="SoundCloud-soitin"
                      className="w-full"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={src}
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  ) : (
                    <a href={a.url} target="_blank" rel="noreferrer" className="underline block p-3 dark:text-white/80 text-black/80 font-customFont1 text-xs">
                      {a.url}
                    </a>
                  )}
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/40 to-transparent" />
                  <button
                    type="button"
                    onClick={() => setConfirmAud({ open: true, item: a })}
                    className="absolute top-2 right-2 z-30 h-8 px-2 text-xs rounded-md bg-black/70 text-white shadow-md ring-1 ring-white/30 hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-pink"
                  >
                    Poista
                  </button>
                  {isNew && <span className="absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-primary/80 text-white">Uusi</span>}
                </li>
              );
            })}
          </ul>
        ) : (
          !scPreviewUrl && <p className="dark:text-white/80 text-black/80 font-customFont1 text-xs">Ei ääniraitoja.</p>
        )}
      </InfoBox>

      {/* Confirmit */}
      <ConfirmDialog
        open={confirmImg.open}
        title="Poistetaanko kuva?"
        description="Tätä toimintoa ei voi perua."
        confirmText="Poista"
        cancelText="Peruuta"
        onConfirm={() => { markRemoveImage(confirmImg.item); setConfirmImg({ open: false, item: null }); }}
        onClose={() => setConfirmImg({ open: false, item: null })}
      />
      <ConfirmDialog
        open={confirmVid.open}
        title="Poistetaanko video?"
        description="Tätä toimintoa ei voi perua."
        confirmText="Poista"
        cancelText="Peruuta"
        onConfirm={() => { markRemoveVideo(confirmVid.item); setConfirmVid({ open: false, item: null }); }}
        onClose={() => setConfirmVid({ open: false, item: null })}
      />
      <ConfirmDialog
        open={confirmAud.open}
        title="Poistetaanko audio?"
        description="Tätä toimintoa ei voi perua."
        confirmText="Poista"
        cancelText="Peruuta"
        onConfirm={() => { markRemoveAudio(confirmAud.item); setConfirmAud({ open: false, item: null }); }}
        onClose={() => setConfirmAud({ open: false, item: null })}
      />
    </div>
  );
}

MediaTab.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  exposeSave: PropTypes.func,
  onSaved: PropTypes.func,
};
